import { faCircleCheck, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { useMemo } from 'react';
import useMeasure from 'react-use-measure';
import { useAppSelector } from 'src/app/hooks';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import type { RoomTask } from 'src/common/interfaces/tasks';
import { VirtualEnvironmentAttackBox } from './virtual-environment-attack-box';
import { getImageSource, getSubtitle } from './virtual-environment-setup.constants';
import { StyledBody, StyledCircleCheckIcon, StyledImage, StyledStaticToast, StyledSubtitle, StyledTitle, StyledVirtualMachinesWrapper, StyledWarningIcon, StyledWrapper } from './virtual-environment-setup.styles';
import { VirtualEnvironmentTargetMachine } from './virtual-environment-target-machine';
type VirtualEnvironmentSetupProps = {
  roomCode: string;
  hasSeenVmStart: boolean;
  task: Omit<RoomTask, 'created' | 'deadline' | 'questions'>;
  hasMultipleVMs: boolean;
};
export const VirtualEnvironmentSetup = ({
  roomCode,
  hasSeenVmStart,
  task,
  hasMultipleVMs
}: VirtualEnvironmentSetupProps) => {
  const [ref, {
    width
  }] = useMeasure();
  const isWide = useMemo(() => width > BREAKPOINTS.MOBILE, [width]);
  const isAttackBoxAvailable = useAppSelector(state => !state.room.remAttackBoxBtn);
  const isAttackboxConnected = useAppSelector(state => !!state.splitScreen.attackBoxPrivateIP);
  const activeMachine = useAppSelector(state => state.room.activeMachine);
  const isAnotherMachineRunning = hasMultipleVMs && !!activeMachine?.uploadId && activeMachine.uploadId !== task.uploadId;
  const isSetupComplete = !!activeMachine && !isAnotherMachineRunning && (!isAttackBoxAvailable || isAttackBoxAvailable && isAttackboxConnected);
  return <div ref={ref} data-sentry-component="VirtualEnvironmentSetup" data-sentry-source-file="virtual-environment-setup.tsx">
      <StyledWrapper roomCode={roomCode} $isWide={isWide} data-sentry-element="StyledWrapper" data-sentry-source-file="virtual-environment-setup.tsx">
        <span>
          <StyledTitle data-sentry-element="StyledTitle" data-sentry-source-file="virtual-environment-setup.tsx">
            {isSetupComplete ? 'Virtual environment set up is complete' : 'Set up your virtual environment'}
          </StyledTitle>
          {isSetupComplete && <StyledCircleCheckIcon icon={faCircleCheck} />}
        </span>
        <StyledSubtitle data-sentry-element="StyledSubtitle" data-sentry-source-file="virtual-environment-setup.tsx">{getSubtitle(isSetupComplete, isAttackBoxAvailable)}</StyledSubtitle>
        {isAnotherMachineRunning && <StyledStaticToast>
            <StyledWarningIcon icon={faTriangleExclamation} />
            You already have a machine running. By starting this machine, the previous machine will be terminated.{' '}
            <br />
            Please ensure you have completed your task on the previous machine before proceeding.
          </StyledStaticToast>}
        <StyledBody data-sentry-element="StyledBody" data-sentry-source-file="virtual-environment-setup.tsx">
          <StyledImage $isWide={isWide} src={getImageSource(isAttackBoxAvailable)} onClick={e => {
          e.stopPropagation();
        }} data-testid="virtual-environment-image" data-sentry-element="StyledImage" data-sentry-source-file="virtual-environment-setup.tsx" />
          <StyledVirtualMachinesWrapper data-sentry-element="StyledVirtualMachinesWrapper" data-sentry-source-file="virtual-environment-setup.tsx">
            {isAttackBoxAvailable && <VirtualEnvironmentAttackBox roomCode={roomCode} isConnected={isAttackboxConnected} />}
            <VirtualEnvironmentTargetMachine roomCode={roomCode} hasSeenVmStart={hasSeenVmStart} task={task} activeMachine={activeMachine} hasMultipleVMs={hasMultipleVMs} isAnotherMachineRunning={isAnotherMachineRunning} data-sentry-element="VirtualEnvironmentTargetMachine" data-sentry-source-file="virtual-environment-setup.tsx" />
          </StyledVirtualMachinesWrapper>
        </StyledBody>
      </StyledWrapper>
    </div>;
};